/**
 * TaxSetu AI - Comprehensive Admin Dashboard
 * Analytics, User Queries, Feature Usage Tracking, Feedback Management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import { 
  Lock, 
  RefreshCw, 
  MessageSquare, 
  Clock, 
  User,
  Download,
  ArrowLeft,
  Eye,
  X,
  TrendingUp,
  Users,
  BarChart3,
  FileText,
  Calculator,
  Scale,
  Heart,
  Activity,
  Calendar,
  Mail,
  UserCheck,
  UserX,
  Star,
  Bug,
  Lightbulb,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Send,
  Crown,
  ClipboardCheck,
  XCircle,
  HelpCircle,
  History,
  MapPin,
  FileSpreadsheet,
  Globe,
  ChevronDown,
  ChevronRight,
  UserCog,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from "../utils/api";
import BlogManager from "../components/BlogManager";

const API_URL = API_BASE_URL;

// TaxSetu AI Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_taxgpt-india-1/artifacts/eq72g3c3_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

export default function AdminPortalPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [queries, setQueries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [queryReport, setQueryReport] = useState(null);
  const [founderIps, setFounderIps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionDraft, setCorrectionDraft] = useState({ answer: "", reason: "", pdf_url: "", notify_user: true });
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [threadView, setThreadView] = useState(null);  // {session_id, queries[]}
  const [threadLoading, setThreadLoading] = useState(false);
  // Staff review workflow state
  const [staffReviewsPending, setStaffReviewsPending] = useState([]);
  const [staffReviewsApproved, setStaffReviewsApproved] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffReviewsPendingCount, setStaffReviewsPendingCount] = useState(0);
  const [newStaffForm, setNewStaffForm] = useState({ email: '', name: '', password: '' });
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewSubTab, setReviewSubTab] = useState('active');
  const [queryReviewStats, setQueryReviewStats] = useState({ pending: 0, validated: 0, needs_correction: 0, irrelevant: 0 });
  const [fundingMetrics, setFundingMetrics] = useState(null);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [newsletterDraft, setNewsletterDraft] = useState(null);
  const [newsletterHistory, setNewsletterHistory] = useState([]);
  const [newsletterTriggering, setNewsletterTriggering] = useState(false);

  // Auto-refresh active users every 30 seconds
  useEffect(() => {
    if (isAuthenticated && password) {
      const refreshActive = async () => {
        try {
          const res = await fetch(`${API_URL}/api/active-users?password=${encodeURIComponent(password)}`);
          if (res.ok) {
            const data = await res.json();
            setActiveUsers(data);
          }
        } catch (e) {}
      };
      
      const interval = setInterval(refreshActive, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, password]);

  // Load newsletter data when tab opens
  useEffect(() => {
    if (!isAuthenticated || !password || activeTab !== "newsletter") return;
    const headers = { "X-Admin-Password": password };
    const loadNewsletter = async () => {
      try {
        const [subs, today, hist] = await Promise.all([
          fetch(`${API_URL}/api/newsletter/admin/subscribers`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/newsletter/admin/today`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/newsletter/admin/sent-history`, { headers }).then(r => r.ok ? r.json() : null),
        ]);
        if (subs) setNewsletterStats(subs);
        if (today) setNewsletterDraft(today);
        if (hist) setNewsletterHistory(hist.history || []);
      } catch (e) {
        toast.error("Failed to load newsletter data");
      }
    };
    loadNewsletter();
  }, [isAuthenticated, password, activeTab]);

  const triggerNewsletterNow = async () => {
    if (!window.confirm("Trigger newsletter pipeline now? It will fetch, curate via AI and send to all active subscribers.")) return;
    setNewsletterTriggering(true);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/admin/trigger-now`, {
        method: "POST",
        headers: { "X-Admin-Password": password },
      });
      const d = await r.json();
      if (d.success) {
        toast.success("Pipeline triggered. Refresh in 60-90 seconds to see results.");
      } else {
        toast.error(d.detail || "Trigger failed");
      }
    } catch (e) {
      toast.error("Trigger failed");
    } finally {
      setNewsletterTriggering(false);
    }
  };

  // Check if already authenticated
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('taxsetu_admin_auth');
    if (savedAuth) {
      setIsAuthenticated(true);
      setPassword(savedAuth);
      fetchData(savedAuth);
    }
  }, []);

  const loadThreadForSession = async (sessionId) => {
    if (!sessionId) return;
    const pwd = localStorage.getItem('taxsetu_admin_pwd') || '';
    if (!pwd) {
      alert('Admin password missing. Please re-authenticate.');
      return;
    }
    setThreadLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/thread/${sessionId}?password=${encodeURIComponent(pwd)}`);
      if (res.ok) {
        const data = await res.json();
        setThreadView(data);
      } else {
        alert('Failed to load thread');
      }
    } catch (err) {
      alert('Error loading thread: ' + err.message);
    } finally {
      setThreadLoading(false);
    }
  };

  const adminApproveReview = async (reviewId, note = '') => {
    const pwd = localStorage.getItem('taxsetu_admin_pwd');
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_id: reviewId, note, admin_password: pwd }),
      });
      if (!res.ok) {
        const d = await res.json();
        toast.error(d.detail || 'Approval failed');
        return;
      }
      toast.success('Review approved ✓');
      setStaffReviewsPending(p => p.filter(r => r.id !== reviewId));
      setStaffReviewsPendingCount(c => Math.max(0, c - 1));
      fetchData(pwd);
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const adminRejectReview = async (reviewId) => {
    const note = prompt('Reason for rejection (optional):') || '';
    const pwd = localStorage.getItem('taxsetu_admin_pwd');
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_id: reviewId, note, admin_password: pwd }),
      });
      if (!res.ok) {
        const d = await res.json();
        toast.error(d.detail || 'Rejection failed');
        return;
      }
      toast.success('Review rejected');
      setStaffReviewsPending(p => p.filter(r => r.id !== reviewId));
      setStaffReviewsPendingCount(c => Math.max(0, c - 1));
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const createStaff = async () => {
    if (!newStaffForm.email || !newStaffForm.password || !newStaffForm.name) {
      toast.error('Please fill all fields');
      return;
    }
    const pwd = localStorage.getItem('taxsetu_admin_pwd');
    try {
      const res = await fetch(`${API_URL}/api/admin/staff/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newStaffForm, admin_password: pwd }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Create failed');
        return;
      }
      toast.success(`Staff ${newStaffForm.email} created`);
      setNewStaffForm({ email: '', name: '', password: '' });
      setShowAddStaff(false);
      fetchData(pwd);
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const toggleStaff = async (email) => {
    const pwd = localStorage.getItem('taxsetu_admin_pwd');
    try {
      const res = await fetch(`${API_URL}/api/admin/staff/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, admin_password: pwd }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Toggle failed');
        return;
      }
      toast.success(`${email} ${data.is_active ? 'activated' : 'deactivated'}`);
      fetchData(pwd);
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const fetchData = async (pwd) => {
    setLoading(true);
    try {
      localStorage.setItem('taxsetu_admin_pwd', pwd);  // Cache for thread fetch
      // Staff workflow data
      try {
        const [pendRes, approvedRes, staffRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/reviews/pending?password=${encodeURIComponent(pwd)}`),
          fetch(`${API_URL}/api/admin/reviews/approved?password=${encodeURIComponent(pwd)}`),
          fetch(`${API_URL}/api/admin/staff/list?password=${encodeURIComponent(pwd)}`),
        ]);
        if (pendRes.ok) {
          const d = await pendRes.json();
          setStaffReviewsPending(d.reviews || []);
          setStaffReviewsPendingCount(d.count || 0);
        }
        if (approvedRes.ok) {
          const d = await approvedRes.json();
          setStaffReviewsApproved(d.corrections || []);
        }
        if (staffRes.ok) {
          const d = await staffRes.json();
          setStaffList(d.staff || []);
        }
      } catch (_) {}
      // Fetch queries
      const queriesRes = await fetch(`${API_URL}/api/admin/all-queries?password=${encodeURIComponent(pwd)}&limit=200`);
      if (queriesRes.ok) {
        const data = await queriesRes.json();
        setQueries(data.queries || []);
      }
      
      // Fetch analytics
      const analyticsRes = await fetch(`${API_URL}/api/admin/analytics?password=${encodeURIComponent(pwd)}`);
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
      
      // Fetch active users
      const activeRes = await fetch(`${API_URL}/api/active-users?password=${encodeURIComponent(pwd)}`);
      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveUsers(data);
      }
      
      // Fetch feedback
      const feedbackRes = await fetch(`${API_URL}/api/admin/feedback?password=${encodeURIComponent(pwd)}`);
      if (feedbackRes.ok) {
        const data = await feedbackRes.json();
        setFeedback(data.feedback || []);
      }
      
      // Fetch query review stats
      const reviewStatsRes = await fetch(`${API_URL}/api/admin/query-stats?password=${encodeURIComponent(pwd)}`);
      if (reviewStatsRes.ok) {
        const data = await reviewStatsRes.json();
        setQueryReviewStats(data);
      }
      
      // Fetch query report (date-wise)
      const reportRes = await fetch(`${API_URL}/api/admin/query-report?password=${encodeURIComponent(pwd)}`);
      if (reportRes.ok) {
        const data = await reportRes.json();
        setQueryReport(data);
      }
      
      // Fetch founder IPs
      const founderRes = await fetch(`${API_URL}/api/founder-ips?password=${encodeURIComponent(pwd)}`);
      if (founderRes.ok) {
        const data = await founderRes.json();
        setFounderIps(data.founder_ips || []);
      }
      
      // Fetch funding metrics (since inception data for investors)
      const fundingRes = await fetch(`${API_URL}/api/admin/funding-metrics?password=${encodeURIComponent(pwd)}`);
      if (fundingRes.ok) {
        const data = await fundingRes.json();
        setFundingMetrics(data);
      }
      
      // Fetch users using simple endpoint (more reliable)
      const usersSimpleRes = await fetch(`${API_URL}/api/admin/users-simple?password=${encodeURIComponent(pwd)}`);
      if (usersSimpleRes.ok) {
        const data = await usersSimpleRes.json();
        if (data.registered_users && data.registered_users.length > 0) {
          // Update analytics with users from simple endpoint
          setAnalytics(prev => ({
            ...prev,
            registered_users: data.registered_users,
            total_registered_users: data.total
          }));
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };
  
  // Export users to Excel/CSV
  const exportUsersToExcel = async () => {
    setExportingUsers(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/export/users?password=${encodeURIComponent(password)}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TaxSetu_AI_Users_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Users exported to Excel successfully!');
      } else {
        toast.error('Failed to export users');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export users');
    }
    setExportingUsers(false);
  };
  
  // Export comprehensive investor report to Excel
  const exportInvestorReport = () => {
    if (!fundingMetrics) {
      toast.error('No investor data to export');
      return;
    }
    setExportingReport(true);
    
    try {
      const si = fundingMetrics.since_inception || {};
      const l30 = fundingMetrics.last_30_days || {};
      const today = fundingMetrics.today || {};
      
      // Create comprehensive CSV with multiple sections
      let csv = "";
      
      // Header
      csv += "TAXSETU AI - GROWTH INSIGHTS REPORT\n";
      csv += `Generated on,${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}\n`;
      csv += `Platform Launch Date,1st February 2026\n\n`;
      
      // Key Metrics Summary
      csv += "=== KEY METRICS SUMMARY ===\n";
      csv += "Metric,Value,Description\n";
      csv += `Platform Launch Date,1st February 2026,Public launch date\n`;
      csv += `Days Since Launch,${Math.floor((new Date() - new Date('2026-02-01')) / (1000 * 60 * 60 * 24))},Days since public launch\n`;
      csv += `Total Queries,${si.total_queries || 0},All-time query count (incl. pre-launch)\n`;
      csv += `Organic Queries,${si.organic_queries || 0},Real user queries\n`;
      csv += `Organic Percentage,${si.organic_percentage || 0}%,Real user engagement rate\n`;
      csv += `Founder Testing,${si.founder_queries || 0},Internal testing queries\n`;
      csv += `Registered Users,${si.total_registered_users || 0},Google/Email sign-ups\n`;
      csv += `Unique Visitors,${si.total_unique_visitors || 0},Unique sessions\n`;
      csv += `Avg Queries/Day,${si.avg_queries_per_day || 0},Daily engagement\n`;
      csv += `Avg Queries/User,${si.avg_queries_per_user || 0},User engagement depth\n\n`;
      
      // Last 30 Days
      csv += "=== LAST 30 DAYS PERFORMANCE ===\n";
      csv += "Metric,Value\n";
      csv += `Queries,${l30.queries || 0}\n`;
      csv += `New Users,${l30.new_users || 0}\n`;
      csv += `Unique Visitors,${l30.unique_visitors || 0}\n\n`;
      
      // Today's Stats
      csv += "=== TODAY'S SNAPSHOT ===\n";
      csv += "Metric,Value\n";
      csv += `Queries Today,${today.queries || 0}\n`;
      csv += `Visitors Today,${today.unique_visitors || 0}\n\n`;
      
      // Daily Visitors (Last 14 Days)
      if (fundingMetrics.daily_visitors && fundingMetrics.daily_visitors.length > 0) {
        csv += "=== DAILY VISITORS (Last 14 Days) ===\n";
        csv += "Date,Day,Visitors,Queries\n";
        fundingMetrics.daily_visitors.forEach(d => {
          csv += `${d.date},${d.day_name},${d.visitors},${d.queries}\n`;
        });
        csv += "\n";
      }
      
      // Monthly Breakdown
      if (fundingMetrics.monthly_breakdown && fundingMetrics.monthly_breakdown.length > 0) {
        csv += "=== MONTHLY BREAKDOWN ===\n";
        csv += "Month,Total Queries,Organic Queries,Founder Queries,New Users,Unique Visitors\n";
        fundingMetrics.monthly_breakdown.forEach(m => {
          csv += `${m.month},${m.queries},${m.organic_queries},${m.founder_queries},${m.new_users},${m.unique_visitors}\n`;
        });
        csv += "\n";
      }
      
      // Feature Usage
      if (fundingMetrics.feature_usage) {
        csv += "=== FEATURE USAGE ===\n";
        csv += "Feature,Usage Count\n";
        Object.entries(fundingMetrics.feature_usage).forEach(([feature, count]) => {
          csv += `${feature},${count}\n`;
        });
        csv += "\n";
      }
      
      // State-wise Distribution
      if (fundingMetrics.state_wise && fundingMetrics.state_wise.length > 0) {
        csv += "=== STATE-WISE VISITOR DISTRIBUTION ===\n";
        csv += "State/Region,Visitors\n";
        fundingMetrics.state_wise.forEach(s => {
          csv += `${s.state || 'Unknown'},${s.visitors}\n`;
        });
        csv += "\n";
      }
      
      // Location data from active users
      if (activeUsers?.geo_distribution) {
        csv += "=== TODAY'S LOCATION BREAKDOWN ===\n";
        csv += "Location,Visitors\n";
        Object.entries(activeUsers.geo_distribution).forEach(([location, count]) => {
          csv += `${location},${count}\n`;
        });
        csv += "\n";
      }
      
      // Growth Indicators
      if (fundingMetrics.growth_indicators) {
        csv += "=== GROWTH INDICATORS ===\n";
        csv += "Metric,Value\n";
        csv += `User Growth Rate,${fundingMetrics.growth_indicators.user_growth_rate || 'N/A'}\n`;
        csv += `Query Growth Rate,${fundingMetrics.growth_indicators.query_growth_rate || 'N/A'}\n`;
      }
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TaxSetu_AI_Investor_Report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Growth Report downloaded! Open in Excel for best viewing.');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export report');
    }
    setExportingReport(false);
  };

  // Export query report to CSV
  const exportQueryReportToCSV = () => {
    if (!queryReport || !queryReport.daily_report) {
      toast.error('No report data to export');
      return;
    }
    
    // Create CSV content
    let csvContent = "Date,Day,Queries,Unique Sessions,Cumulative Total\n";
    queryReport.daily_report.forEach(day => {
      csvContent += `${day.date},${day.day_name},${day.queries},${day.unique_sessions},${day.cumulative_total}\n`;
    });
    
    // Add summary
    csvContent += "\n\nSUMMARY\n";
    csvContent += `Total Queries Since Inception,${queryReport.summary.total_queries_since_inception}\n`;
    csvContent += `Start Date,${queryReport.summary.start_date}\n`;
    csvContent += `End Date,${queryReport.summary.end_date}\n`;
    csvContent += `Total Days,${queryReport.summary.total_days}\n`;
    csvContent += `Average Queries Per Day,${queryReport.summary.avg_queries_per_day}\n`;
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `TaxSetu_Query_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Query report exported to CSV!');
  };

  const submitQueryCorrection = async () => {
    if (!selectedQuery?.id) return;
    if (!correctionDraft.answer || correctionDraft.answer.trim().length < 20) {
      toast.error("Corrected answer must be at least 20 characters");
      return;
    }
    setCorrectionSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/queries/${selectedQuery.id}/correct?password=${encodeURIComponent(password)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            corrected_answer: correctionDraft.answer,
            reason: correctionDraft.reason,
            pdf_url: correctionDraft.pdf_url,
            admin_email: "admin",
            notify_user: correctionDraft.notify_user,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success(
        `Correction saved · Chat updated: ${data.chat_message_updated ? "yes" : "no"} · Email: ${data.email_sent ? "sent" : "skipped"}`
      );
      // Update local state so admin sees updated response immediately
      setQueries(prev => prev.map(q => q.id === selectedQuery.id
        ? { ...q, full_response: correctionDraft.answer, response_preview: correctionDraft.answer.slice(0, 500), review_status: "needs_correction" }
        : q
      ));
      setSelectedQuery(prev => prev ? { ...prev, full_response: correctionDraft.answer, response_preview: correctionDraft.answer.slice(0, 500), review_status: "needs_correction" } : prev);
      setCorrectionMode(false);
      setCorrectionDraft({ answer: "", reason: "", pdf_url: "", notify_user: true });
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setCorrectionSaving(false);
    }
  };

  const updateQueryReviewStatus = async (queryId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/query-status/${queryId}?password=${encodeURIComponent(password)}&status=${status}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success(`Query marked as ${status}`);
        // Update local state immediately for responsive UI
        setQueries(prev => prev.map(q => 
          q.id === queryId ? { ...q, review_status: status } : q
        ));
        // Update stats
        setQueryReviewStats(prev => {
          const oldStatus = queries.find(q => q.id === queryId)?.review_status || 'pending';
          return {
            ...prev,
            [oldStatus]: Math.max(0, prev[oldStatus] - 1),
            [status]: prev[status] + 1
          };
        });
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      console.error('Update query status error:', err);
      toast.error('Failed to update status');
    }
  };

  const updateFeedbackStatus = async (feedbackId, status, response = '') => {
    try {
      await fetch(`${API_URL}/api/admin/feedback/${feedbackId}?password=${encodeURIComponent(password)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_response: response })
      });
      fetchData(password);
      setSelectedFeedback(null);
      setAdminResponse('');
    } catch (err) {
      console.error('Update feedback error:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/all-queries?password=${encodeURIComponent(password)}&limit=200`);
      
      if (response.ok) {
        const data = await response.json();
        setQueries(data.queries || []);
        setIsAuthenticated(true);
        sessionStorage.setItem('taxsetu_admin_auth', password);
        fetchData(password);
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setQueries([]);
    setAnalytics(null);
    sessionStorage.removeItem('taxsetu_admin_auth');
  };

  const exportToCSV = () => {
    if (queries.length === 0) return;
    
    const headers = ['Time (IST)', 'User', 'Email', 'Query', 'Type', 'Feature', 'Full Response'];
    const csvContent = [
      headers.join(','),
      ...queries.map(q => [
        formatDateIST(q.timestamp),
        q.user_name || (q.user_type === 'guest' ? 'Guest User' : 'Unknown'),
        q.user_email || 'N/A',
        `"${(q.query || '').replace(/"/g, '""')}"`,
        q.query_type || 'general',
        q.feature || 'chat',
        `"${(q.full_response || q.response_preview || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taxsetu_queries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Convert UTC to Indian Standard Time (IST = UTC+5:30)
  const formatDateIST = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    // Add 5 hours 30 minutes for IST
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) + ' IST';
  };

  // Filter queries by feature
  const [featureFilter, setFeatureFilter] = useState('all');
  const filteredQueries = featureFilter === 'all' 
    ? queries 
    : queries.filter(q => (q.feature || 'chat') === featureFilter);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0A1A30' }}>
        <div className="w-full max-w-md">
          <div className="bg-slate-800/50 backdrop-blur-lg rounded-2xl p-8 border border-slate-700 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                <img src={TAXSETU_LOGO} alt="TaxSetu AI" className="w-12 h-12 object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">TaxSetu AI Admin</h1>
              <p className="text-slate-400 text-sm">Enter your admin password to access analytics</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Admin Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="Enter password..."
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center bg-red-500/10 py-2 rounded-lg">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin mx-auto" /> : 'Access Dashboard'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => alert('📧 Contact: camittal.shivangi@gmail.com\n💡 Hint: TaxSetu@[Word][Year]')}
                className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors underline"
              >
                Forgot Password?
              </button>
            </div>

            <button
              onClick={() => navigate('/')}
              className="mt-6 w-full text-center text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A1A30' }}>
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <img src={TAXSETU_LOGO} alt="TaxSetu AI" className="w-8 h-8 object-contain" />
              <h1 className="text-xl font-bold text-white">TaxSetu AI Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/set-founder-ip?password=${encodeURIComponent(password)}`, { method: 'POST' });
                  const data = await res.json();
                  if (data.status === 'success') {
                    toast.success(`Your IP marked as founder: ${data.full_ip}`);
                    fetchData(password);
                  } else {
                    toast.error(data.message || 'Failed');
                  }
                } catch (e) {
                  toast.error('Failed to set founder IP');
                }
              }} 
              variant="outline" 
              size="sm" 
              className="text-amber-400 border-amber-500/50 hover:bg-amber-500/10"
            >
              <Crown className="w-4 h-4 mr-2" />
              Mark My IP
            </Button>
            {founderIps.length > 0 && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <Crown className="w-3 h-3 text-amber-400" />
                <span className="text-xs text-amber-400">Marked IPs: </span>
                <span className="text-xs text-amber-300 font-mono">{founderIps.join(', ')}</span>
              </div>
            )}
            <Button onClick={() => fetchData(password)} variant="outline" size="sm" className="text-slate-300 border-slate-600">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={exportToCSV} variant="outline" size="sm" className="text-slate-300 border-slate-600">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <ThemeToggle />
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 border-b border-slate-700 pb-4 overflow-x-auto">
          {[
            { id: 'blog', label: 'Law & Blogs', icon: FileText, highlight: true },
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'investors', label: 'Growth Insights', icon: TrendingUp },
            { id: 'activity', label: 'Traffic & Activity', icon: Activity },
            { id: 'queries', label: 'User Queries', icon: MessageSquare },
            { id: 'review', label: 'Query Review', icon: ClipboardCheck, badge: queryReviewStats.pending },
            { id: 'feedback', label: 'Feedback', icon: Star, badge: feedback.filter(f => f.status === 'new').length },
            { id: 'users', label: 'Registered Users', icon: Users },
            { id: 'newsletter', label: 'Newsletter', icon: Mail },
            { id: 'staff_reviews', label: 'Staff Reviews', icon: ClipboardCheck, badge: staffReviewsPendingCount },
            { id: 'staff_management', label: 'Staff Management', icon: UserCog }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? (tab.highlight ? 'bg-amber-500 text-slate-900 font-semibold' : 'bg-cyan-600 text-white')
                  : tab.highlight
                    ? 'text-amber-300 border border-amber-500/40 hover:bg-amber-500/10'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Activity Tab - Visitors vs Queries */}
        {activeTab === 'activity' && analytics && (
          <div className="space-y-6">
            {/* Real-time Active Users */}
            <div className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 rounded-xl p-6 border border-green-500/30">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                Real-time Active Users
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-4xl font-bold text-green-400">{activeUsers?.active_now || 0}</p>
                  <p className="text-sm text-slate-400">Active Now (5 min)</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-4xl font-bold text-cyan-400">{activeUsers?.active_15_mins || 0}</p>
                  <p className="text-sm text-slate-400">Last 15 mins</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-4xl font-bold text-blue-400">{activeUsers?.total_today || 0}</p>
                  <p className="text-sm text-slate-400">Total Today</p>
                </div>
              </div>
              
              {/* Active Users List */}
              {activeUsers?.active_list?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Currently Active Visitors:</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {activeUsers.active_list.map((visitor, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg text-sm border ${
                        visitor.is_founder 
                          ? 'bg-amber-500/10 border-amber-500/50' 
                          : 'bg-slate-800/50 border-slate-700/50'
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full animate-pulse ${visitor.is_founder ? 'bg-amber-400' : 'bg-green-400'}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              {visitor.is_founder ? (
                                <>
                                  <span className="text-amber-400 font-mono font-bold">{visitor.ip_full || visitor.ip_masked}</span>
                                  <span className="px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full font-bold">👑 FOUNDER</span>
                                </>
                              ) : (
                                <span className="text-slate-300 font-mono">{visitor.ip_masked}</span>
                              )}
                            </div>
                            <span className="text-xs text-cyan-400">
                              📍 {visitor.city !== 'Unknown' ? `${visitor.city}, ` : ''}{visitor.region}, {visitor.country}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-right">
                          <div>
                            <span className="text-slate-400 block">{visitor.page_views} views</span>
                            <span className="text-xs text-slate-600">{visitor.isp}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Geo Distribution */}
              {activeUsers?.geo_distribution && Object.keys(activeUsers.geo_distribution).length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">📊 Today's Visitors by Location:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(activeUsers.geo_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 6)
                      .map(([location, count], idx) => (
                        <div key={idx} className="p-2 bg-slate-800/50 rounded-lg border border-slate-700/50 flex items-center justify-between">
                          <span className="text-xs text-slate-300 truncate">{location}</span>
                          <span className="text-sm font-bold text-cyan-400 ml-2">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Investor Metrics - Query Breakdown */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                📊 Query Breakdown - Organic vs Marked IPs
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                {fundingMetrics?.since_inception?.start_date 
                  ? `Since ${fundingMetrics.since_inception.start_date} (${fundingMetrics.since_inception.days_active} days)`
                  : 'Real users vs founder testing queries'}
              </p>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                  <p className="text-4xl font-bold text-white">{fundingMetrics?.since_inception?.total_queries || analytics.overview?.total_queries || 0}</p>
                  <p className="text-sm text-slate-400">Total Queries</p>
                  <p className="text-xs text-slate-500 mt-1">Since Inception</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-lg border border-green-500/30">
                  <p className="text-4xl font-bold text-green-400">{fundingMetrics?.since_inception?.organic_queries || analytics.investor_metrics?.organic_queries || 0}</p>
                  <p className="text-sm text-slate-400">Organic (Real Users)</p>
                  <p className="text-xs text-green-400/70 mt-1">{fundingMetrics?.since_inception?.organic_percentage || analytics.investor_metrics?.organic_percentage || 0}% of total</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-lg border border-amber-500/30">
                  <p className="text-4xl font-bold text-amber-400">{fundingMetrics?.since_inception?.founder_queries || analytics.investor_metrics?.founder_queries || 0}</p>
                  <p className="text-sm text-slate-400">Marked IPs (Testing)</p>
                  <p className="text-xs text-amber-400/70 mt-1">Founder's queries</p>
                </div>
              </div>

              {/* Additional Investor KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-cyan-400">{fundingMetrics?.today?.unique_visitors || analytics.investor_metrics?.unique_users_today || 0}</p>
                  <p className="text-xs text-slate-400">Today's Visitors</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-pink-400">{fundingMetrics?.since_inception?.avg_queries_per_day || analytics.investor_metrics?.avg_queries_per_session || 0}</p>
                  <p className="text-xs text-slate-400">Avg Queries/Day</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-400">{fundingMetrics?.since_inception?.total_registered_users || analytics.investor_metrics?.registered_users || 0}</p>
                  <p className="text-xs text-slate-400">Registered Users</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{fundingMetrics?.last_30_days?.queries || 0}</p>
                  <p className="text-xs text-slate-400">Last 30 Days</p>
                </div>
              </div>
            </div>

            {/* State-wise Breakdown from Funding Metrics */}
            {fundingMetrics?.state_wise && fundingMetrics.state_wise.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                  State-wise Visitors (Since Inception)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {fundingMetrics.state_wise.map((state, idx) => (
                    <div key={idx} className="p-4 bg-slate-900/50 rounded-lg border border-slate-600 text-center hover:border-cyan-500/50 transition-colors">
                      <p className="text-2xl font-bold text-cyan-400">{state.visitors}</p>
                      <p className="text-xs text-slate-400 truncate" title={state.state}>{state.state || 'Unknown'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Unique Visitors" value={activeUsers?.total_today || analytics.overview?.unique_sessions || 0} color="blue" />
              <StatCard icon={MessageSquare} label="Total Queries" value={analytics.overview?.total_queries || 0} color="cyan" />
              <StatCard icon={Activity} label="Today's Queries" value={analytics.overview?.today_queries || 0} color="green" />
              <StatCard icon={UserCheck} label="Registered Users" value={analytics.overview?.total_registered_users || analytics.registered_users?.length || 0} color="purple" />
            </div>

            {/* Visitors vs Queries Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Visitor to Query Conversion
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-blue-400" />
                      <div>
                        <p className="text-white font-semibold">Unique Visitors</p>
                        <p className="text-xs text-slate-400">People who visited the site</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-blue-400">{activeUsers?.total_today || analytics.overview?.unique_sessions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-6 h-6 text-cyan-400" />
                      <div>
                        <p className="text-white font-semibold">Users Who Asked Queries</p>
                        <p className="text-xs text-slate-400">Actually interacted with AI</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-cyan-400">{analytics.overview?.total_queries || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-white font-semibold">Conversion Rate</p>
                        <p className="text-xs text-slate-400">Visitors who asked queries</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-green-400">
                      {analytics.overview?.unique_sessions > 0 
                        ? Math.round((analytics.overview?.total_queries / analytics.overview?.unique_sessions) * 100) + '%'
                        : '0%'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* User Type Breakdown */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-cyan-400" />
                  User Type Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <div className="flex items-center gap-3">
                      <UserX className="w-6 h-6 text-orange-400" />
                      <div>
                        <p className="text-white font-semibold">Guest Users</p>
                        <p className="text-xs text-slate-400">"Try as Guest" users</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-orange-400">{analytics.user_breakdown?.guest || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-white font-semibold">Registered Users</p>
                        <p className="text-xs text-slate-400">Google Sign-in users</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-green-400">{analytics.user_breakdown?.registered || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-purple-400" />
                      <div>
                        <p className="text-white font-semibold">Total Registered</p>
                        <p className="text-xs text-slate-400">All registered accounts</p>
                      </div>
                    </div>
                    <span className="text-3xl font-bold text-purple-400">{analytics.registered_users?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hourly Activity Chart */}
            {analytics.hourly_activity && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  Today's Hourly Activity (IST)
                </h3>
                <div className="flex items-end gap-1 h-40">
                  {analytics.hourly_activity.map((h, idx) => {
                    const maxCount = Math.max(...analytics.hourly_activity.map(x => x.count), 1);
                    const height = (h.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group relative">
                        <div className="absolute -top-6 hidden group-hover:block bg-slate-900 px-2 py-1 rounded text-xs text-white whitespace-nowrap z-10">
                          {h.hour}: {h.count} queries
                        </div>
                        <div 
                          className={`w-full rounded-t transition-all cursor-pointer ${h.count > 0 ? 'bg-cyan-500 hover:bg-cyan-400' : 'bg-slate-700'}`}
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        {idx % 4 === 0 && (
                          <span className="text-[10px] text-slate-500 mt-1">{h.hour}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>12 AM</span>
                  <span>6 AM</span>
                  <span>12 PM</span>
                  <span>6 PM</span>
                  <span>11 PM</span>
                </div>
              </div>
            )}

            {/* Daily Stats Chart */}
            {analytics.daily_stats && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                  Last 7 Days Activity
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {analytics.daily_stats.reverse().map((day, idx) => (
                    <div key={idx} className="text-center">
                      <div className="h-24 flex items-end justify-center mb-2">
                        <div 
                          className={`w-full max-w-[40px] rounded-t ${day.count > 0 ? 'bg-gradient-to-t from-cyan-600 to-cyan-400' : 'bg-slate-700'}`}
                          style={{ height: `${Math.max((day.count / Math.max(...analytics.daily_stats.map(d => d.count), 1)) * 100, 8)}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-400">{day.date}</p>
                      <p className="text-sm font-bold text-white">{day.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            {/* Total Since Inception Banner */}
            {queryReport && (
              <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-6 border border-cyan-500/30">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                      <BarChart3 className="w-7 h-7 text-cyan-400" />
                      Total Queries Since Inception
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">
                      From {queryReport.summary?.start_date} to {queryReport.summary?.end_date} ({queryReport.summary?.total_days} days)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-5xl font-bold text-cyan-400">{queryReport.summary?.total_queries_since_inception || 0}</p>
                    <p className="text-slate-400 text-sm">{queryReport.summary?.avg_queries_per_day || 0} avg/day</p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={MessageSquare} label="Total Queries" value={queryReport?.summary?.total_queries_since_inception || analytics.overview?.total_queries || 0} color="cyan" />
              <StatCard icon={Activity} label="Today" value={analytics.overview?.today_queries || 0} color="green" />
              <StatCard icon={Calendar} label="This Week" value={analytics.overview?.week_queries || 0} color="purple" />
              <StatCard icon={Users} label="Unique Sessions" value={analytics.overview?.unique_sessions || 0} color="amber" />
            </div>

            {/* Date-wise Query Report - Calendar Style */}
            {queryReport && queryReport.daily_report && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    Date-wise Query Report
                  </h3>
                  <Button 
                    onClick={exportQueryReportToCSV} 
                    variant="outline" 
                    size="sm" 
                    className="text-green-400 border-green-500/50 hover:bg-green-500/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                </div>
                
                {/* Monthly Summary */}
                {queryReport.monthly_summary && queryReport.monthly_summary.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Monthly Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {queryReport.monthly_summary.slice(0, 4).map((month, idx) => (
                        <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                          <p className="text-xs text-slate-500">{month.month_display}</p>
                          <p className="text-2xl font-bold text-white">{month.queries}</p>
                          <p className="text-xs text-slate-400">{month.avg_per_day} avg/day</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Daily Table */}
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="text-left text-slate-400 border-b border-slate-700">
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Day</th>
                        <th className="py-2 px-3 text-right">Queries</th>
                        <th className="py-2 px-3 text-right">Sessions</th>
                        <th className="py-2 px-3 text-right">Cumulative</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queryReport.daily_report.slice(0, 30).map((day, idx) => (
                        <tr key={idx} className={`border-b border-slate-700/50 ${day.queries > 0 ? '' : 'opacity-50'}`}>
                          <td className="py-2 px-3 text-white">{day.date_display}</td>
                          <td className="py-2 px-3 text-slate-400">{day.day_name}</td>
                          <td className="py-2 px-3 text-right">
                            <span className={`font-bold ${day.queries > 10 ? 'text-green-400' : day.queries > 0 ? 'text-cyan-400' : 'text-slate-600'}`}>
                              {day.queries}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right text-slate-400">{day.unique_sessions}</td>
                          <td className="py-2 px-3 text-right text-purple-400">{day.cumulative_total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-3 text-center">Showing last 30 days. Export to Excel for full history.</p>
              </div>
            )}

            {/* User Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-400" />
                  User Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserX className="w-5 h-5 text-orange-400" />
                      <span className="text-slate-300">Guest Users (Try as Guest)</span>
                    </div>
                    <span className="text-2xl font-bold text-orange-400">{analytics.user_breakdown?.guest || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-green-400" />
                      <span className="text-slate-300">Registered Users (Google)</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">{analytics.user_breakdown?.registered || 0}</span>
                  </div>
                </div>
              </div>

              {/* Feature Usage */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Feature Usage
                </h3>
                <div className="space-y-3">
                  <FeatureBar label="AI Chat" value={analytics.feature_usage?.chat || 0} icon={MessageSquare} color="cyan" />
                  <FeatureBar label="Notice Analyzer" value={analytics.feature_usage?.notice_analyzer || 0} icon={FileText} color="red" />
                  <FeatureBar label="Donations/80G" value={analytics.feature_usage?.donations || 0} icon={Heart} color="pink" />
                  <FeatureBar label="Compare Tool" value={analytics.feature_usage?.compare || 0} icon={Scale} color="purple" />
                  <FeatureBar label="Calculator" value={analytics.feature_usage?.calculator || 0} icon={Calculator} color="green" />
                  <FeatureBar label="PractiQ GST" value={analytics.feature_usage?.practiq_gst || 0} icon={Activity} color="amber" />
                </div>
              </div>
            </div>

            {/* Top Queries */}
            {analytics.top_queries && analytics.top_queries.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Most Asked Questions
                </h3>
                <div className="space-y-2">
                  {analytics.top_queries.slice(0, 5).map((q, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                      <span className="text-slate-300 text-sm">{q.query}</span>
                      <span className="text-cyan-400 font-medium">{q.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Queries Tab */}
        {activeTab === 'queries' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-white">User Queries</h3>
                <p className="text-slate-400 text-sm">All queries asked by users (Time in IST)</p>
              </div>
              {/* Filter Dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">Filter:</span>
                <select
                  value={featureFilter}
                  onChange={(e) => setFeatureFilter(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="all">All Features ({queries.length})</option>
                  <option value="chat">💬 AI Chat ({queries.filter(q => !q.feature || q.feature === 'chat').length})</option>
                  <option value="notice">📄 Notice Analyzer ({queries.filter(q => q.feature === 'notice').length})</option>
                  <option value="donations">❤️ Donations/80G ({queries.filter(q => q.feature === 'donations').length})</option>
                  <option value="compare">⚖️ Compare Tool ({queries.filter(q => q.feature === 'compare').length})</option>
                  <option value="calculator">🧮 Calculator ({queries.filter(q => q.feature === 'calculator').length})</option>
                  <option value="practiq">📋 PractiQ GST ({queries.filter(q => q.feature === 'practiq').length})</option>
                </select>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Time (IST)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Query</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Feature</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredQueries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                        No queries found for this filter
                      </td>
                    </tr>
                  ) : (
                    filteredQueries.map((query, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/50">
                        <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                          {formatDateIST(query.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-white">
                              {query.user_name || (query.user_type === 'guest' ? '👤 Guest User' : 'Unknown')}
                            </p>
                            <p className="text-xs text-slate-500">
                              {query.user_email || (query.user_type === 'guest' ? '(Try as Guest)' : 'N/A')}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2 max-w-md">
                            {query.is_follow_up && (
                              <span
                                className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-500/20 text-purple-300 border border-purple-500/40 mt-0.5"
                                title={`Follow-up · ${query.follow_up_confidence}% · ${query.follow_up_reason}`}
                              >
                                ↳ FOLLOW-UP
                              </span>
                            )}
                            <p className="text-sm text-slate-300 truncate flex-1">{query.query}</p>
                            {query.attachment_count > 0 && (
                              <span
                                className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 mt-0.5"
                                title={`${query.attachment_count} attachment(s)`}
                              >
                                📎 {query.attachment_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            query.feature === 'notice' ? 'bg-red-500/20 text-red-400' :
                            query.feature === 'donations' ? 'bg-pink-500/20 text-pink-400' :
                            query.feature === 'compare' ? 'bg-purple-500/20 text-purple-400' :
                            query.feature === 'calculator' ? 'bg-green-500/20 text-green-400' :
                            query.feature === 'practiq' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-cyan-500/20 text-cyan-400'
                          }`}>
                            {query.feature || 'chat'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            query.query_type === 'tiger_global' ? 'bg-red-500/20 text-red-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {query.query_type || 'general'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelectedQuery(query)}
                            className="text-cyan-400 hover:text-cyan-300 transition-colors p-2 hover:bg-cyan-500/10 rounded-lg"
                            title="View Full Response"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-slate-700 text-center text-slate-500 text-sm">
              Showing {filteredQueries.length} of {queries.length} queries
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-white">User Feedback</h3>
                <p className="text-slate-400 text-sm">Review and respond to user suggestions (Time in IST)</p>
              </div>
              <div className="flex gap-2 text-sm flex-wrap">
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded flex items-center gap-1">
                  <span>🏆</span> Beta Founders: {feedback.filter(f => f.is_beta_founder).length}
                </span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">New: {feedback.filter(f => f.status === 'new').length}</span>
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">In Progress: {feedback.filter(f => f.status === 'in_progress').length}</span>
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Resolved: {feedback.filter(f => f.status === 'resolved').length}</span>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Time (IST)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Feedback</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {feedback.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                        No feedback yet. Share the feedback button with users!
                      </td>
                    </tr>
                  ) : (
                    feedback.map((fb, idx) => (
                      <tr key={idx} className={`hover:bg-slate-800/50 ${fb.is_beta_founder ? 'border-l-2 border-l-amber-500' : ''}`}>
                        <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                          {formatDateIST(fb.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {fb.is_beta_founder && <span title="Beta Founder">🏆</span>}
                            <div>
                              <p className="text-sm text-white">
                                {fb.is_anonymous ? '👤 Anonymous' : (fb.name || 'User')}
                              </p>
                              <p className="text-xs text-cyan-400">
                                {fb.is_anonymous ? '(Anonymous)' : (fb.email || 'No email')}
                              </p>
                              {fb.coupon_code && (
                                <p className="text-xs text-amber-400 font-mono">{fb.coupon_code}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            fb.feedback_type === 'bug' ? 'bg-red-500/20 text-red-400' :
                            fb.feedback_type === 'feature' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-cyan-500/20 text-cyan-400'
                          }`}>
                            {fb.feedback_type === 'bug' && <Bug className="w-3 h-3 inline mr-1" />}
                            {fb.feedback_type === 'feature' && <Lightbulb className="w-3 h-3 inline mr-1" />}
                            {fb.feedback_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex">
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} className={`w-4 h-4 ${s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-300 max-w-xs truncate">{fb.message}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            fb.status === 'new' ? 'bg-green-500/20 text-green-400' :
                            fb.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {fb.status === 'resolved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                            {fb.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => { setSelectedFeedback(fb); setAdminResponse(fb.admin_response || ''); }}
                            className="text-cyan-400 hover:text-cyan-300 transition-colors p-2 hover:bg-cyan-500/10 rounded-lg"
                            title="Respond to Feedback"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Query Review Tab */}
        {activeTab === 'review' && (
          <div className="space-y-6">
            {/* Summary Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                icon={Clock} 
                label="Pending Review" 
                value={queryReviewStats.pending || 0} 
                color="amber" 
              />
              <StatCard 
                icon={CheckCircle} 
                label="Validated" 
                value={queryReviewStats.validated || 0} 
                color="green" 
              />
              <StatCard 
                icon={AlertCircle} 
                label="Needs Correction" 
                value={queryReviewStats.needs_correction || 0} 
                color="red" 
              />
              <StatCard 
                icon={XCircle} 
                label="Irrelevant" 
                value={queryReviewStats.irrelevant || 0} 
                color="slate" 
              />
            </div>

            {/* Sub-tabs for Active vs Reviewed */}
            <div className="flex gap-2 bg-slate-800/50 p-2 rounded-lg w-fit">
              <button
                onClick={() => setReviewSubTab('active')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  reviewSubTab === 'active' 
                    ? 'bg-amber-600 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <Clock className="w-4 h-4" />
                Active Queries ({queries.filter(q => !q.review_status || q.review_status === 'pending').length})
              </button>
              <button
                onClick={() => setReviewSubTab('reviewed')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  reviewSubTab === 'reviewed' 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <History className="w-4 h-4" />
                Reviewed History ({queries.filter(q => q.review_status && q.review_status !== 'pending').length})
              </button>
            </div>

            {/* Active Queries - Pending Review */}
            {reviewSubTab === 'active' && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    Pending Review
                  </h3>
                  <p className="text-slate-400 text-sm">AI responses that need your validation</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User Query</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">AI Response (Preview)</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {queries.filter(q => !q.review_status || q.review_status === 'pending').length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                            All queries have been reviewed!
                          </td>
                        </tr>
                      ) : (
                        queries.filter(q => !q.review_status || q.review_status === 'pending').slice(0, 50).map((query, idx) => (
                          <tr key={idx} className="hover:bg-slate-800/50">
                            <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                              {formatDateIST(query.timestamp)}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-white max-w-xs truncate" title={query.query}>
                                {query.query}
                              </p>
                              <p className="text-xs text-slate-500">
                                {query.user_name || (query.user_type === 'guest' ? 'Guest' : 'Unknown')}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-slate-300 max-w-md truncate" title={query.full_response || query.response_preview}>
                                {(query.full_response || query.response_preview || '').substring(0, 150)}...
                              </p>
                              <button
                                onClick={() => setSelectedQuery(query)}
                                className="text-xs text-cyan-400 hover:text-cyan-300 mt-1"
                              >
                                View Full Response →
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => updateQueryReviewStatus(query.id, 'validated')}
                                  className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                  title="Mark as Validated"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => updateQueryReviewStatus(query.id, 'needs_correction')}
                                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                  title="Needs Correction"
                                >
                                  <AlertCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => updateQueryReviewStatus(query.id, 'irrelevant')}
                                  className="p-2 bg-slate-500/20 text-slate-400 rounded-lg hover:bg-slate-500/30 transition-colors"
                                  title="Mark as Irrelevant"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {queries.filter(q => !q.review_status || q.review_status === 'pending').length > 50 && (
                  <div className="p-3 border-t border-slate-700 text-center text-slate-500 text-sm">
                    Showing 50 of {queries.filter(q => !q.review_status || q.review_status === 'pending').length} pending queries
                  </div>
                )}
              </div>
            )}

            {/* Reviewed History */}
            {reviewSubTab === 'reviewed' && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-cyan-400" />
                    Reviewed History
                  </h3>
                  <p className="text-slate-400 text-sm">Previously reviewed queries with their status</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User Query</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {queries.filter(q => q.review_status && q.review_status !== 'pending').length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                            <History className="w-12 h-12 mx-auto mb-2 text-slate-600" />
                            No reviewed queries yet
                          </td>
                        </tr>
                      ) : (
                        queries.filter(q => q.review_status && q.review_status !== 'pending').slice(0, 100).map((query, idx) => (
                          <tr key={idx} className="hover:bg-slate-800/50">
                            <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">
                              {formatDateIST(query.timestamp)}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-white max-w-md truncate" title={query.query}>
                                {query.query}
                              </p>
                              <p className="text-xs text-slate-500">
                                {query.user_name || (query.user_type === 'guest' ? 'Guest' : 'Unknown')}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                                query.review_status === 'validated' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : query.review_status === 'needs_correction'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-slate-500/20 text-slate-400'
                              }`}>
                                {query.review_status === 'validated' && <CheckCircle className="w-3 h-3" />}
                                {query.review_status === 'needs_correction' && <AlertCircle className="w-3 h-3" />}
                                {query.review_status === 'irrelevant' && <XCircle className="w-3 h-3" />}
                                {query.review_status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => setSelectedQuery(query)}
                                className="text-cyan-400 hover:text-cyan-300 transition-colors p-2 hover:bg-cyan-500/10 rounded-lg"
                                title="View Full Response"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Investor Metrics Tab - For Funding Presentations */}
        {activeTab === 'investors' && (
          <div className="space-y-6">
            {/* Hero Banner - Clean Cyan Theme */}
            <div className="bg-gradient-to-r from-cyan-600/20 via-teal-500/15 to-emerald-500/10 rounded-xl p-8 border border-cyan-500/30">
              <div className="flex items-center justify-between flex-wrap gap-6">
                <div>
                  <div className="flex items-center gap-4 mb-3">
                    <img src={TAXSETU_LOGO} alt="TaxSetu AI" className="w-16 h-16 object-contain" />
                    <div>
                      <h2 className="text-3xl font-bold text-white">TaxSetu AI</h2>
                      <p className="text-cyan-400 text-lg font-medium">Growth Insights</p>
                    </div>
                  </div>
                  <p className="text-slate-300 text-base">
                    Platform Launch: <span className="text-cyan-400 font-semibold">1st February 2026</span>
                    {' '}({Math.floor((new Date() - new Date('2026-02-01')) / (1000 * 60 * 60 * 24))} days since public launch)
                  </p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="text-right">
                    <p className="text-6xl font-bold text-cyan-400">
                      {fundingMetrics?.since_inception?.total_queries || queryReport?.summary?.total_queries_since_inception || 0}
                    </p>
                    <p className="text-slate-400 text-base">Total Queries Since Inception</p>
                  </div>
                  <Button 
                    onClick={exportInvestorReport}
                    disabled={exportingReport}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {exportingReport ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                    )}
                    Download Growth Report
                  </Button>
                </div>
              </div>
            </div>

            {/* Key Metrics Grid - Clean Colors */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-6 border border-emerald-500/30">
                <p className="text-sm text-emerald-400 mb-1 font-medium">Organic Queries</p>
                <p className="text-4xl font-bold text-emerald-400">{fundingMetrics?.since_inception?.organic_queries || 0}</p>
                <p className="text-xs text-emerald-400/70 mt-1">{fundingMetrics?.since_inception?.organic_percentage || 0}% Real Users</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-6 border border-amber-500/30">
                <p className="text-sm text-amber-400 mb-1 font-medium">Testing Queries</p>
                <p className="text-4xl font-bold text-amber-400">{fundingMetrics?.since_inception?.founder_queries || 0}</p>
                <p className="text-xs text-amber-400/70 mt-1">Founder/Internal</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl p-6 border border-cyan-500/30">
                <p className="text-sm text-cyan-400 mb-1 font-medium">Registered Users</p>
                <p className="text-4xl font-bold text-cyan-400">{fundingMetrics?.since_inception?.total_registered_users || 0}</p>
                <p className="text-xs text-cyan-400/70 mt-1">Google/Email Sign-ups</p>
              </div>
              <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 rounded-xl p-6 border border-teal-500/30">
                <p className="text-sm text-teal-400 mb-1 font-medium">Unique Visitors</p>
                <p className="text-4xl font-bold text-teal-400">{fundingMetrics?.since_inception?.total_unique_visitors || 0}</p>
                <p className="text-xs text-teal-400/70 mt-1">Unique Sessions</p>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 text-center">
                <p className="text-3xl font-bold text-white">{fundingMetrics?.since_inception?.avg_queries_per_day || 0}</p>
                <p className="text-sm text-slate-400">Avg Queries/Day</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 text-center">
                <p className="text-3xl font-bold text-white">{fundingMetrics?.since_inception?.avg_queries_per_user || 0}</p>
                <p className="text-sm text-slate-400">Avg Queries/User</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 text-center">
                <p className="text-3xl font-bold text-cyan-400">{fundingMetrics?.today?.queries || 0}</p>
                <p className="text-sm text-slate-400">Today's Queries</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 text-center">
                <p className="text-3xl font-bold text-emerald-400">{fundingMetrics?.today?.unique_visitors || 0}</p>
                <p className="text-sm text-slate-400">Today's Visitors</p>
              </div>
            </div>

            {/* Location Distribution - Today's Visitors */}
            {activeUsers?.geo_distribution && Object.keys(activeUsers.geo_distribution).length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-cyan-400" />
                  Today's Visitors by Location
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {Object.entries(activeUsers.geo_distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([location, count], idx) => (
                      <div key={idx} className="p-4 bg-slate-900/50 rounded-lg border border-slate-600 text-center hover:border-cyan-500/50 transition-colors">
                        <p className="text-2xl font-bold text-cyan-400">{count}</p>
                        <p className="text-xs text-slate-400 truncate" title={location}>{location}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Daily Visitors - Month-wise Accordion */}
            {fundingMetrics?.monthly_daily && fundingMetrics.monthly_daily.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Daily Visitor Breakdown
                </h3>
                <div className="space-y-3">
                  {fundingMetrics.monthly_daily.map((monthData, mIdx) => (
                    <div key={mIdx} className="border border-slate-600 rounded-lg overflow-hidden">
                      {/* Month Header - Clickable */}
                      <button
                        onClick={() => setExpandedMonths(prev => ({ ...prev, [monthData.month]: !prev[monthData.month] }))}
                        className="w-full p-4 flex items-center justify-between bg-slate-700/50 hover:bg-slate-700 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform ${expandedMonths[monthData.month] ? 'rotate-180' : ''}`} />
                          <span className="text-white font-semibold">{monthData.month}</span>
                          {monthData.month.includes('January') && (
                            <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Pre-launch Beta</span>
                          )}
                          {monthData.month.includes('February') && (
                            <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">Public Launch</span>
                          )}
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-slate-400">Visitors: <span className="text-cyan-400 font-bold">{monthData.total_visitors}</span></span>
                          <span className="text-slate-400">Queries: <span className="text-emerald-400 font-bold">{monthData.total_queries}</span></span>
                        </div>
                      </button>
                      
                      {/* Days Table - Expandable */}
                      {expandedMonths[monthData.month] && (
                        <div className="p-4 bg-slate-800/30">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-slate-400 border-b border-slate-700 text-xs">
                                <th className="py-2 px-3 text-left w-8"></th>
                                <th className="py-2 px-3 text-left">Date</th>
                                <th className="py-2 px-3 text-left">Day</th>
                                <th className="py-2 px-3 text-right">Visitors</th>
                                <th className="py-2 px-3 text-right">Queries</th>
                                <th className="py-2 px-3 text-right">Cumulative (Since Inception)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...monthData.days].reverse().map((day, dIdx) => (
                                <React.Fragment key={dIdx}>
                                  <tr 
                                    className={`border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer ${day.visitors > 0 ? '' : 'opacity-50'}`}
                                    onClick={() => day.visitors > 0 && setExpandedDays(prev => ({ ...prev, [day.date]: !prev[day.date] }))}
                                  >
                                    <td className="py-2 px-3">
                                      {day.visitors > 0 && (
                                        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${expandedDays[day.date] ? 'rotate-90' : ''}`} />
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-white">{day.date_display}</td>
                                    <td className="py-2 px-3 text-slate-400">{day.day_name}</td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={`font-medium ${day.visitors > 0 ? 'text-cyan-400' : 'text-slate-500'}`}>
                                        {day.visitors}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={`${day.queries > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                                        {day.queries}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right text-amber-400 font-medium">{day.cumulative}</td>
                                  </tr>
                                  {/* State-wise Breakdown Row */}
                                  {expandedDays[day.date] && day.visitors > 0 && (
                                    <tr className="bg-slate-900/50">
                                      <td colSpan="6" className="p-3">
                                        <div className="flex items-center gap-2 mb-2">
                                          <MapPin className="w-4 h-4 text-teal-400" />
                                          <span className="text-xs text-slate-400">State-wise Breakdown for {day.date_display}:</span>
                                        </div>
                                        {activeUsers?.geo_distribution && Object.keys(activeUsers.geo_distribution).length > 0 ? (
                                          <div className="flex flex-wrap gap-2">
                                            {Object.entries(activeUsers.geo_distribution).map(([loc, count], lIdx) => (
                                              <span key={lIdx} className="px-3 py-1 bg-slate-700 rounded text-xs text-slate-300">
                                                {loc}: <span className="text-teal-400 font-medium">{count}</span>
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-slate-500 italic">Location data available for today only</p>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly Breakdown */}
            {fundingMetrics?.monthly_breakdown && fundingMetrics.monthly_breakdown.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    Monthly Performance
                  </h3>
                  <div className="flex gap-4 text-sm">
                    <span className="text-slate-400">Total Visitors: <span className="text-cyan-400 font-bold">{fundingMetrics.monthly_breakdown.reduce((sum, m) => sum + (m.unique_visitors || 0), 0)}</span></span>
                    <span className="text-slate-400">Total Queries: <span className="text-emerald-400 font-bold">{fundingMetrics.monthly_breakdown.reduce((sum, m) => sum + (m.queries || 0), 0)}</span></span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-700">
                        <th className="py-3 px-4">Month</th>
                        <th className="py-3 px-4 text-right">Total Queries</th>
                        <th className="py-3 px-4 text-right">Organic</th>
                        <th className="py-3 px-4 text-right">Testing</th>
                        <th className="py-3 px-4 text-right">New Users</th>
                        <th className="py-3 px-4 text-right">Visitors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundingMetrics.monthly_breakdown.map((month, idx) => (
                        <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4 text-white font-medium">
                            {month.month}
                            {month.month.includes('January') && (
                              <span className="ml-2 text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Pre-launch Beta</span>
                            )}
                            {month.month.includes('February') && (
                              <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">Public Launch</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right text-cyan-400 font-bold">{month.queries}</td>
                          <td className="py-3 px-4 text-right text-emerald-400">{month.organic_queries}</td>
                          <td className="py-3 px-4 text-right text-amber-400">{month.founder_queries}</td>
                          <td className="py-3 px-4 text-right text-cyan-400">{month.new_users}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{month.unique_visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Feature Usage */}
            {fundingMetrics?.feature_usage && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  Feature Adoption (Since Inception)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(fundingMetrics.feature_usage).map(([feature, count], idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-600 hover:border-cyan-500/50 transition-colors">
                      <span className="text-slate-300">{feature}</span>
                      <span className="text-xl font-bold text-cyan-400">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* State-wise Visitors Distribution */}
            {fundingMetrics?.state_wise && fundingMetrics.state_wise.length > 0 && (
              <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 rounded-xl p-6 border border-teal-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-400" />
                  State-wise Visitor Distribution (Since Inception)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {fundingMetrics.state_wise.map((state, idx) => (
                    <div key={idx} className="p-4 bg-slate-800/50 rounded-lg border border-slate-600 text-center hover:border-teal-500/50 transition-colors">
                      <p className="text-2xl font-bold text-teal-400">{state.visitors}</p>
                      <p className="text-xs text-slate-400 truncate" title={state.state}>{state.state || 'Unknown'}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-4 text-center">
                  Total States: {fundingMetrics.state_wise.length} | 
                  Total Visitors: {fundingMetrics.state_wise.reduce((sum, s) => sum + s.visitors, 0)}
                </p>
              </div>
            )}

            {/* Growth Metrics - Last 30 Days */}
            {fundingMetrics?.growth_indicators && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-xl p-6 border border-emerald-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  Last 30 Days Performance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-3xl font-bold text-emerald-400">{fundingMetrics.last_30_days?.queries || 0}</p>
                    <p className="text-sm text-slate-400">Queries</p>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-3xl font-bold text-cyan-400">{fundingMetrics.last_30_days?.new_users || 0}</p>
                    <p className="text-sm text-slate-400">New Users</p>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-3xl font-bold text-teal-400">{fundingMetrics.last_30_days?.unique_visitors || 0}</p>
                    <p className="text-sm text-slate-400">Unique Visitors</p>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-3xl font-bold text-amber-400">{fundingMetrics.growth_indicators?.query_growth_rate || 'N/A'}</p>
                    <p className="text-sm text-slate-400">Growth Rate</p>
                  </div>
                </div>
              </div>
            )}

            {/* No Data State */}
            {!fundingMetrics && (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 text-slate-600 mx-auto mb-4 animate-spin" />
                <p className="text-slate-400">Loading investor metrics...</p>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && analytics && (
          <div className="space-y-6">
            {/* User Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                icon={Users} 
                label="Total Registered" 
                value={analytics.overview?.total_registered_users || analytics.registered_users?.length || 0} 
                color="cyan" 
              />
              <StatCard 
                icon={UserCheck} 
                label="Active Now" 
                value={analytics.registered_users?.filter(u => u.is_active).length || 0} 
                color="green" 
              />
              <StatCard 
                icon={UserX} 
                label="Signed Out" 
                value={analytics.registered_users?.filter(u => !u.is_active).length || 0} 
                color="slate" 
              />
              <StatCard 
                icon={Activity} 
                label="Unique Sessions Today" 
                value={analytics.overview?.unique_sessions || 0} 
                color="purple" 
              />
            </div>

            {/* Hourly Activity Chart */}
            {analytics.hourly_activity && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  Today's Hourly Activity (IST)
                </h3>
                <div className="flex items-end gap-1 h-32">
                  {analytics.hourly_activity.map((h, idx) => {
                    const maxCount = Math.max(...analytics.hourly_activity.map(x => x.count), 1);
                    const height = (h.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group">
                        <div 
                          className={`w-full rounded-t transition-all ${h.count > 0 ? 'bg-cyan-500' : 'bg-slate-700'}`}
                          style={{ height: `${Math.max(height, 4)}%` }}
                          title={`${h.hour}: ${h.count} queries`}
                        />
                        {idx % 3 === 0 && (
                          <span className="text-[10px] text-slate-500 mt-1">{h.hour}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>12 AM</span>
                  <span>6 AM</span>
                  <span>12 PM</span>
                  <span>6 PM</span>
                  <span>12 AM</span>
                </div>
              </div>
            )}

            {/* Registered Users Table */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    Registered Users (Since Inception)
                  </h3>
                  <p className="text-slate-400 text-sm">All users who signed in with Google or Email OTP</p>
                </div>
                <Button 
                  onClick={exportUsersToExcel}
                  disabled={exportingUsers}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {exportingUsers ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                  )}
                  Download Excel
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Queries</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined (IST)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Last Login</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {analytics.registered_users?.map((user, idx) => (
                      <tr key={idx} className={`hover:bg-slate-800/50 ${user.is_active ? 'bg-green-500/5' : ''}`}>
                        <td className="px-4 py-3">
                          {user.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-slate-600/20 text-slate-400">
                              <span className="w-2 h-2 bg-slate-500 rounded-full" />
                              Signed Out
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                                <User className="w-4 h-4 text-cyan-400" />
                              </div>
                            )}
                            <span className="text-sm text-white font-medium">{user.name || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-cyan-400">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400">
                            <MessageSquare className="w-3 h-3" />
                            {user.query_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">{formatDateIST(user.created_at)}</td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {user.last_login ? formatDateIST(user.last_login) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {user.last_activity ? formatDateIST(user.last_activity) : 'No activity'}
                        </td>
                      </tr>
                    ))}
                    {(!analytics.registered_users || analytics.registered_users.length === 0) && (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                          No registered users yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Query Details Modal - Full Response View */}
      {selectedQuery && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuery(null)}>
          <div className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900/50">
              <h3 className="text-lg font-semibold text-white">📋 Query Details (Full View)</h3>
              <button onClick={() => setSelectedQuery(null)} className="text-slate-400 hover:text-white p-1 hover:bg-slate-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-70px)]">
              {/* User Info */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg p-4 border border-cyan-500/30">
                <p className="text-xs text-cyan-400 mb-1 font-medium">👤 USER INFO</p>
                <p className="text-white font-semibold text-lg">
                  {selectedQuery.user_name || (selectedQuery.user_type === 'guest' ? '👤 Guest User' : 'Unknown')}
                </p>
                <p className="text-cyan-400 text-sm">
                  {selectedQuery.user_email || (selectedQuery.user_type === 'guest' ? '(Try as Guest - No email captured)' : 'N/A')}
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  🕐 {formatDateIST(selectedQuery.timestamp)}
                </p>
              </div>

              {/* Query */}
              <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-amber-400 font-medium">❓ USER QUERY</p>
                  <div className="flex items-center gap-2">
                    {selectedQuery.is_follow_up && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40"
                        title={selectedQuery.follow_up_reason}
                      >
                        ↳ FOLLOW-UP · {selectedQuery.follow_up_confidence}%
                      </span>
                    )}
                    {selectedQuery.session_id && (
                      <button
                        onClick={() => loadThreadForSession(selectedQuery.session_id)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 transition-colors"
                        title="View all queries in this session — each still counts separately for quota"
                        data-testid="view-thread-btn"
                      >
                        🧵 View Full Thread
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-white text-base leading-relaxed">{selectedQuery.query}</p>
                {selectedQuery.is_follow_up && (
                  <p className="text-[11px] text-purple-300/80 mt-2 italic">
                    Detected because: {selectedQuery.follow_up_reason}
                  </p>
                )}
              </div>

              {/* Attachments */}
              {selectedQuery.attachments && selectedQuery.attachments.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-4 border border-amber-500/30">
                  <p className="text-xs text-amber-400 mb-3 font-medium">
                    📎 ATTACHMENTS ({selectedQuery.attachments.length})
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedQuery.attachments.map((att, i) => {
                      const isImage = (att.content_type || '').startsWith('image/');
                      const url = att.url ? `${API_URL}${att.url}` : '#';
                      return (
                        <div key={i} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                          <div className="flex items-center gap-3 mb-2">
                            {isImage ? (
                              <img src={url} alt={att.original_name} className="w-16 h-16 object-cover rounded border border-slate-600" />
                            ) : (
                              <div className="w-16 h-16 flex items-center justify-center rounded bg-red-500/10 border border-red-500/30 text-red-400 text-2xl">
                                📄
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate font-medium">{att.original_name}</p>
                              <p className="text-[11px] text-slate-400">
                                {att.content_type} · {((att.size_bytes || 0) / 1024).toFixed(1)} KB
                              </p>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
                              >
                                Open in new tab ↗
                              </a>
                            </div>
                          </div>
                          {att.extracted_text_preview && (
                            <details className="text-[11px] text-slate-400 mt-1">
                              <summary className="cursor-pointer text-slate-300 hover:text-cyan-300">
                                Extracted text preview
                              </summary>
                              <pre className="mt-2 p-2 bg-slate-950/50 rounded text-[10px] whitespace-pre-wrap max-h-32 overflow-y-auto">
                                {att.extracted_text_preview}
                              </pre>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Full AI Response - THIS IS THE IMPORTANT PART */}
              <div className="bg-slate-700/50 rounded-lg p-4 border border-green-500/30">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs text-green-400 font-medium">🤖 TAXSETU AI RESPONSE (FULL)</p>
                  {!correctionMode ? (
                    <button
                      onClick={() => {
                        setCorrectionDraft({
                          answer: selectedQuery.full_response || selectedQuery.response_preview || "",
                          reason: "",
                          pdf_url: "",
                          notify_user: !!selectedQuery.user_email,
                        });
                        setCorrectionMode(true);
                      }}
                      className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-3 py-1 rounded-md border border-amber-500/30"
                      data-testid="edit-ai-response-btn"
                    >
                      ✎ Correct this answer
                    </button>
                  ) : (
                    <button
                      onClick={() => setCorrectionMode(false)}
                      className="text-xs bg-slate-600 hover:bg-slate-500 text-slate-200 px-3 py-1 rounded-md"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {!correctionMode && (
                  <div className="bg-slate-900/50 rounded-lg p-4 max-h-[500px] overflow-y-auto">
                    <div className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">
                      {selectedQuery.full_response || selectedQuery.response_preview || 'Response not recorded'}
                    </div>
                  </div>
                )}

                {correctionMode && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-amber-300 block mb-1">Corrected answer *</label>
                      <textarea
                        value={correctionDraft.answer}
                        onChange={(e) => setCorrectionDraft({ ...correctionDraft, answer: e.target.value })}
                        rows={12}
                        className="w-full bg-slate-900 border border-slate-700 rounded-md p-3 text-sm text-slate-100 leading-relaxed resize-y"
                        data-testid="correction-answer-input"
                        placeholder="Rewrite the correct answer as TaxSetu AI would give it…"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Reason for correction</label>
                        <input
                          type="text"
                          value={correctionDraft.reason}
                          onChange={(e) => setCorrectionDraft({ ...correctionDraft, reason: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                          placeholder="e.g. missed the Cabinet approval dated 15 Sep 2026"
                          data-testid="correction-reason-input"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Reference PDF / URL (optional)</label>
                        <input
                          type="url"
                          value={correctionDraft.pdf_url}
                          onChange={(e) => setCorrectionDraft({ ...correctionDraft, pdf_url: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                          placeholder="https://cbdt.gov.in/notification-45-2026.pdf"
                          data-testid="correction-pdf-input"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-[12px] text-slate-300">
                      <input
                        type="checkbox"
                        checked={correctionDraft.notify_user}
                        onChange={(e) => setCorrectionDraft({ ...correctionDraft, notify_user: e.target.checked })}
                        className="rounded"
                        disabled={!selectedQuery.user_email}
                      />
                      Email the user about this correction {selectedQuery.user_email ? `(${selectedQuery.user_email})` : "— no email on file"}
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                      <button
                        onClick={submitQueryCorrection}
                        disabled={correctionSaving}
                        className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50"
                        data-testid="correction-submit-btn"
                      >
                        {correctionSaving ? "Saving…" : "Save correction & notify"}
                      </button>
                    </div>
                    <p className="text-[10.5px] text-slate-500 leading-snug">
                      Saving will: (1) update the answer in the user's chat, (2) store this correction so future similar queries answer correctly,
                      {selectedQuery.user_email && correctionDraft.notify_user ? " (3) email the user with the corrected answer." : ""}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Metadata */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                  <p className="text-xs text-slate-400 mb-1">FEATURE</p>
                  <p className="text-white capitalize">{selectedQuery.feature || 'chat'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                  <p className="text-xs text-slate-400 mb-1">TYPE</p>
                  <p className="text-white">{selectedQuery.query_type || 'general'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                  <p className="text-xs text-slate-400 mb-1">SESSION ID</p>
                  <p className="text-white text-xs font-mono">{selectedQuery.session_id?.substring(0, 16) || 'N/A'}...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Response Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFeedback(null)}>
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b border-slate-700 ${
              selectedFeedback.is_beta_founder 
                ? 'bg-gradient-to-r from-amber-600/20 to-orange-600/20' 
                : 'bg-gradient-to-r from-purple-600/20 to-pink-600/20'
            }`}>
              <div className="flex items-center gap-2">
                {selectedFeedback.is_beta_founder && <span className="text-xl">🏆</span>}
                <h3 className="text-lg font-semibold text-white">
                  {selectedFeedback.is_beta_founder ? 'Beta Founder Feedback' : '📝 Respond to Feedback'}
                </h3>
              </div>
              <button onClick={() => setSelectedFeedback(null)} className="text-slate-400 hover:text-white p-1 hover:bg-slate-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-70px)]">
              {/* Beta Founder Badge */}
              {selectedFeedback.is_beta_founder && (
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-400 font-semibold flex items-center gap-2">
                        🏆 Beta Founders Club Member
                      </p>
                      <p className="text-slate-400 text-sm mt-1">Rewards: Premium Pass, Voting Rights, Free ITR-1</p>
                    </div>
                    {selectedFeedback.coupon_code && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Coupon Code:</p>
                        <p className="font-mono text-amber-400 font-bold">{selectedFeedback.coupon_code}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User Info */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-semibold">
                      {selectedFeedback.is_anonymous ? '👤 Anonymous User' : (selectedFeedback.name || 'User')}
                    </p>
                    {!selectedFeedback.is_anonymous && selectedFeedback.email && (
                      <p className="text-cyan-400 text-sm">{selectedFeedback.email}</p>
                    )}
                    <p className="text-slate-400 text-xs mt-1">{formatDateIST(selectedFeedback.created_at)}</p>
                  </div>
                  <div className="flex">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-5 h-5 ${s <= selectedFeedback.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Feedback Message */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedFeedback.feedback_type === 'bug' ? 'bg-red-500/20 text-red-400' :
                    selectedFeedback.feedback_type === 'feature' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-cyan-500/20 text-cyan-400'
                  }`}>
                    {selectedFeedback.feedback_type}
                  </span>
                  <span className="text-slate-500 text-xs">Page: {selectedFeedback.page || 'Unknown'}</span>
                </div>
                <p className="text-white">{selectedFeedback.message}</p>
              </div>

              {/* Beta Founders Extra Data */}
              {selectedFeedback.is_beta_founder && (
                <div className="grid grid-cols-2 gap-3">
                  {selectedFeedback.feature_vote && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <p className="text-xs text-purple-400 mb-1">🗳️ Feature Vote</p>
                      <p className="text-white font-medium">
                        {selectedFeedback.feature_vote === 'itr4' ? 'ITR-4 Filing' : 'GST Audit'}
                      </p>
                    </div>
                  )}
                  {selectedFeedback.usage_frequency && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                      <p className="text-xs text-cyan-400 mb-1">📊 Usage Frequency</p>
                      <p className="text-white font-medium">{selectedFeedback.usage_frequency}</p>
                    </div>
                  )}
                  {selectedFeedback.improvement_suggestion && (
                    <div className="col-span-2 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <p className="text-xs text-green-400 mb-1">💡 Improvement Suggestion</p>
                      <p className="text-white">{selectedFeedback.improvement_suggestion}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Admin Response */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Your Response (will be emailed to user if not anonymous)</label>
                <textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Write your response... Let the user know you've heard them!"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-none"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => updateFeedbackStatus(selectedFeedback.id, 'in_progress', adminResponse)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Mark In Progress
                </Button>
                <Button
                  onClick={() => updateFeedbackStatus(selectedFeedback.id, 'resolved', adminResponse)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Resolved
                </Button>
              </div>

              {!selectedFeedback.is_anonymous && selectedFeedback.email && (
                <p className="text-xs text-slate-500 text-center">
                  💡 When marked as resolved, user will be notified at {selectedFeedback.email}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

        {/* Blog Manager Tab — controls the top ticker + landing-page blog grid */}
        {activeTab === 'blog' && (
          <div data-testid="admin-blog-tab">
            <BlogManager password={password} />
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <div className="space-y-6" data-testid="admin-newsletter-tab">
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Mail} label="Total Subscribers" value={newsletterStats?.total || 0} color="cyan" />
              <StatCard icon={Mail} label="Active" value={newsletterStats?.active || 0} color="emerald" />
              <StatCard icon={Mail} label="Today's Stories" value={newsletterDraft?.draft?.stories?.length || 0} color="violet" />
              <StatCard icon={Mail} label="Last Send Status" value={newsletterDraft?.sent_log ? `${newsletterDraft.sent_log.sent_count}/${newsletterDraft.sent_log.sent_count + (newsletterDraft.sent_log.failed_count || 0)}` : '—'} color="amber" />
            </div>

            {/* Trigger button */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-white font-semibold mb-1">Manual Pipeline Trigger</h3>
                  <p className="text-xs text-slate-400">Fetches latest RSS, runs AI curation, sends to all active subscribers immediately. Otherwise, auto-runs daily at 9:00 AM IST.</p>
                </div>
                <Button
                  onClick={triggerNewsletterNow}
                  disabled={newsletterTriggering}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="newsletter-trigger-now-btn"
                >
                  {newsletterTriggering ? "Triggering…" : "🚀 Trigger Now"}
                </Button>
              </div>
            </div>

            {/* Today's draft preview */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Today's Draft</h3>
                {newsletterDraft?.draft?.subject && (
                  <span className="text-xs text-slate-400">{newsletterDraft.draft.date}</span>
                )}
              </div>
              {newsletterDraft?.draft ? (
                <div>
                  <div className="text-sm text-cyan-300 font-medium mb-3" data-testid="newsletter-draft-subject">
                    📩 {newsletterDraft.draft.subject}
                  </div>
                  <p className="text-xs text-slate-400 mb-4 italic">{newsletterDraft.draft.intro}</p>
                  <div className="space-y-2">
                    {(newsletterDraft.draft.stories || []).map((s, i) => (
                      <div key={i} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                          <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300">{s.category}</span>
                          <span>·</span>
                          <span>{s.source}</span>
                        </div>
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-white hover:text-cyan-300 block">
                          {s.headline}
                        </a>
                        <p className="text-xs text-slate-400 mt-1">{s.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No draft generated yet for today. Click "Trigger Now" or wait for 9:00 AM IST schedule.</p>
              )}
            </div>

            {/* Sent history */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-3">Sent History (Last 60)</h3>
              {newsletterHistory.length === 0 ? (
                <p className="text-sm text-slate-500">No sends recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs border-b border-slate-700">
                        <th className="text-left py-2 pr-3">Date</th>
                        <th className="text-left py-2 pr-3">Subject</th>
                        <th className="text-right py-2 pr-3">Stories</th>
                        <th className="text-right py-2 pr-3">Sent</th>
                        <th className="text-right py-2">Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterHistory.map((h, i) => (
                        <tr key={i} className="border-b border-slate-800 hover:bg-slate-900/30">
                          <td className="py-2 pr-3 text-slate-300">{h.date}</td>
                          <td className="py-2 pr-3 text-slate-200 max-w-md truncate">{h.subject}</td>
                          <td className="py-2 pr-3 text-right text-cyan-300">{h.story_count}</td>
                          <td className="py-2 pr-3 text-right text-emerald-400 font-semibold">{h.sent_count}</td>
                          <td className="py-2 text-right text-rose-400">{h.failed_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Subscribers list */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-3">Subscribers ({newsletterStats?.total || 0})</h3>
              {(newsletterStats?.subscribers || []).length === 0 ? (
                <p className="text-sm text-slate-500">No subscribers yet.</p>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="text-slate-400 text-xs border-b border-slate-700">
                        <th className="text-left py-2 pr-3">Email</th>
                        <th className="text-left py-2 pr-3">Name</th>
                        <th className="text-left py-2 pr-3">Preferences</th>
                        <th className="text-left py-2 pr-3">Subscribed</th>
                        <th className="text-right py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(newsletterStats.subscribers || []).map((s, i) => (
                        <tr key={i} className="border-b border-slate-800 hover:bg-slate-900/30">
                          <td className="py-2 pr-3 text-slate-200">{s.email}</td>
                          <td className="py-2 pr-3 text-slate-400">{s.name || '—'}</td>
                          <td className="py-2 pr-3 text-xs text-slate-400">{(s.preferences || []).join(', ') || 'All'}</td>
                          <td className="py-2 pr-3 text-xs text-slate-500">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}</td>
                          <td className="py-2 text-right">
                            {s.is_active ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-300">Active</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-400">Unsubscribed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── STAFF REVIEWS TAB ──────────────────────────────────────── */}
        {activeTab === 'staff_reviews' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Staff Review Queue</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Suggestions submitted by staff awaiting your approval
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 font-semibold">
                  {staffReviewsPending.length} pending
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold">
                  {staffReviewsApproved.length} approved
                </span>
              </div>
            </div>

            {staffReviewsPending.length === 0 ? (
              <div className="text-center py-16 bg-slate-800/40 rounded-lg border border-slate-700">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p className="text-slate-400">No pending reviews. All caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {staffReviewsPending.map((r) => (
                  <div
                    key={r.id}
                    className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 hover:border-cyan-500/40 transition"
                    data-testid={`pending-review-${r.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/40">
                        {r.action.replace(/_/g, ' ')}
                      </span>
                      <div className="flex items-center gap-2 text-[11px] text-slate-400">
                        <span>by {r.staff_name || r.staff_email}</span>
                        <span>•</span>
                        <span>{r.created_at?.substring(0, 19).replace('T', ' ')}</span>
                      </div>
                    </div>
                    <p className="text-sm text-white font-medium mb-2">
                      Q: {r.original_query}
                    </p>
                    <details className="mb-2">
                      <summary className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer">
                        Original AI response ↓
                      </summary>
                      <div className="mt-2 p-3 bg-slate-950/60 rounded text-[12px] text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {(r.original_response || '').substring(0, 1500)}
                      </div>
                    </details>
                    <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30 mb-2">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1 font-semibold">
                        ✓ Suggested Answer
                      </p>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{r.suggested_answer}</p>
                    </div>
                    {r.reasoning && (
                      <p className="text-xs text-slate-400 italic mb-2">
                        <strong className="text-cyan-300">Reasoning:</strong> {r.reasoning}
                      </p>
                    )}
                    {r.references && (
                      <p className="text-xs text-slate-400 mb-2">
                        <strong className="text-cyan-300">References:</strong> {r.references}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700">
                      <button
                        onClick={() => adminApproveReview(r.id, '')}
                        className="px-4 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/60 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/30"
                        data-testid={`approve-${r.id}`}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => adminRejectReview(r.id)}
                        className="px-4 py-1.5 rounded-lg bg-rose-500/20 border border-rose-500/60 text-rose-300 text-sm font-semibold hover:bg-rose-500/30"
                        data-testid={`reject-${r.id}`}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {staffReviewsApproved.length > 0 && (
              <div className="mt-8">
                <h4 className="text-base font-bold text-white mb-3">
                  ✓ Approved Knowledge Updates
                </h4>
                <div className="space-y-2">
                  {staffReviewsApproved.slice(0, 10).map((c) => (
                    <div
                      key={c.id}
                      className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-3"
                    >
                      <p className="text-sm text-white font-medium mb-1">Q: {c.original_query}</p>
                      <details className="text-xs text-slate-400">
                        <summary className="cursor-pointer text-emerald-400 hover:text-emerald-300">
                          Approved correction ↓
                        </summary>
                        <p className="mt-2 p-2 bg-slate-950/40 rounded text-slate-300 whitespace-pre-wrap">
                          {c.corrected_answer}
                        </p>
                      </details>
                      <p className="text-[10px] text-slate-500 mt-1">
                        By {c.submitted_by_staff} · {c.approved_at?.substring(0, 19).replace('T', ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── STAFF MANAGEMENT TAB ────────────────────────────────────── */}
        {activeTab === 'staff_management' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Staff Management</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Create and manage staff accounts who can review queries
                </p>
              </div>
              <button
                onClick={() => setShowAddStaff(!showAddStaff)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold hover:opacity-90"
                data-testid="add-staff-btn"
              >
                <Plus className="w-4 h-4" /> Add Staff
              </button>
            </div>

            {showAddStaff && (
              <div className="bg-slate-800/60 border border-cyan-500/30 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-white">Create New Staff Account</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newStaffForm.name}
                    onChange={(e) => setNewStaffForm(p => ({ ...p, name: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm outline-none focus:border-cyan-500"
                    data-testid="new-staff-name"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newStaffForm.email}
                    onChange={(e) => setNewStaffForm(p => ({ ...p, email: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm outline-none focus:border-cyan-500"
                    data-testid="new-staff-email"
                  />
                  <input
                    type="text"
                    placeholder="Initial Password"
                    value={newStaffForm.password}
                    onChange={(e) => setNewStaffForm(p => ({ ...p, password: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm outline-none focus:border-cyan-500"
                    data-testid="new-staff-password"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={createStaff}
                    className="px-4 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/60 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/30"
                    data-testid="create-staff-confirm"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => setShowAddStaff(false)}
                    className="px-4 py-1.5 text-sm text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 border-b border-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Name / Email</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Status</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Total</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Pending</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Approved</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Last Login</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {staffList.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-500">No staff accounts yet</td></tr>
                  ) : staffList.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{s.name}</p>
                          <p className="text-xs text-slate-400">{s.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${s.is_active ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'}`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-300">{s.total_reviews}</td>
                      <td className="px-4 py-3 text-center text-amber-300">{s.pending_admin}</td>
                      <td className="px-4 py-3 text-center text-emerald-300">{s.approved}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{s.last_login ? s.last_login.substring(0, 19).replace('T', ' ') : 'Never'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => toggleStaff(s.email)}
                          className="text-xs px-3 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                        >
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Thread Modal — view full conversation context */}
        {threadView && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setThreadView(null)}
            data-testid="thread-modal"
          >
            <div
              className="bg-slate-800 border border-cyan-500/30 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-gradient-to-r from-cyan-600/20 to-blue-600/20">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    🧵 Full Conversation Thread
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Session: <code className="text-cyan-300">{threadView.session_id?.substring(0, 24)}…</code>
                    {' · '}
                    <span className="text-cyan-300 font-semibold">{threadView.thread_count}</span> queries
                    {' (each counts separately for quota)'}
                  </p>
                </div>
                <button onClick={() => setThreadView(null)} className="text-slate-400 hover:text-white p-1 hover:bg-slate-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-3 overflow-y-auto">
                {threadView.queries.map((q, idx) => {
                  const indent = q.is_follow_up ? "ml-6 border-l-2 border-purple-500/40 pl-4" : "";
                  return (
                    <div
                      key={q.id || idx}
                      className={`bg-slate-900/40 border border-slate-700 rounded-lg p-3 ${indent}`}
                      data-testid={`thread-item-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-mono">#{idx + 1}</span>
                          {q.is_follow_up ? (
                            <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/40 font-semibold uppercase tracking-wider">
                              ↳ Follow-up · {q.follow_up_confidence}%
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-semibold uppercase tracking-wider">
                              ✓ New Topic
                            </span>
                          )}
                          {q.attachments && q.attachments.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-semibold">
                              📎 {q.attachments.length}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-500">{q.timestamp?.substring(0, 19).replace('T', ' ')}</span>
                      </div>
                      <p className="text-sm text-white font-medium leading-snug">{q.query}</p>
                      {q.response_preview && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-cyan-400 cursor-pointer hover:text-cyan-300">Show AI response →</summary>
                          <div className="mt-2 p-3 bg-slate-950/50 rounded text-[12px] text-slate-300 whitespace-pre-wrap leading-relaxed">
                            {(q.full_response || q.response_preview).substring(0, 1500)}
                            {(q.full_response || '').length > 1500 && '…'}
                          </div>
                        </details>
                      )}
                      {q.is_follow_up && q.follow_up_reason && (
                        <p className="text-[10px] text-purple-300/70 mt-1.5 italic">↳ {q.follow_up_reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-2 border-t border-slate-700 bg-slate-900/50 text-[11px] text-slate-400">
                💡 {threadView.note}
              </div>
            </div>
          </div>
        )}

        {threadLoading && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
            <div className="bg-slate-800 px-6 py-4 rounded-lg text-cyan-300">Loading thread…</div>
          </div>
        )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30 text-red-400',
    slate: 'from-slate-500/20 to-slate-600/10 border-slate-500/30 text-slate-400',
    pink: 'from-pink-500/20 to-pink-600/10 border-pink-500/30 text-pink-400',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400'
  };
  
  const colorClass = colors[color] || colors.cyan;
  const iconColor = colorClass.split(' ').pop();

  return (
    <div className={`bg-gradient-to-br ${colorClass} border rounded-xl p-4`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-8 h-8 ${iconColor}`} />
        <div>
          <p className="text-3xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Feature Bar Component
function FeatureBar({ label, value, icon: Icon, color }) {
  const colors = {
    cyan: 'bg-cyan-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    pink: 'bg-pink-500'
  };

  const maxVal = 100; // For visualization
  const width = Math.min((value / maxVal) * 100, 100);

  return (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 text-${color}-400`} />
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-sm text-slate-300">{label}</span>
          <span className="text-sm font-medium text-white">{value}</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${colors[color]} rounded-full transition-all`} style={{ width: `${width}%` }} />
        </div>
      </div>
    </div>
  );
}
